// ============================================================
// RoleSwitcher — seamless multi-role switching for HornDeliver.
// ------------------------------------------------------------
// Two variants:
//   • "dropdown" — desktop top-right dropdown (in the header)
//   • "sidebar" — inline in the sidebar footer
//   • "bar" — mobile sticky tab bar
//
// Non-admin users can switch between customer / provider / driver.
// Admin always stays in admin mode (no switcher shown).
// ============================================================

import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Store as StoreIcon,
  Truck,
  ChevronDown,
  RefreshCw,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { Role } from "@/lib/types";
import { ROLE_META, effectiveRole } from "@/lib/navigation";

// Roles available for switching (non-admin).
const SWITCHABLE_ROLES: { role: Role; icon: LucideIcon; labelKey: string; label: string }[] = [
  { role: "customer", icon: Package, labelKey: "nav.sender", label: "Sender" },
  { role: "provider", icon: StoreIcon, labelKey: "nav.providerTab", label: "Provider" },
  { role: "driver", icon: Truck, labelKey: "nav.courier", label: "Courier" },
];

// ============================================================
// RoleSwitcher — multi-variant component.
// ============================================================
export function RoleSwitcher({
  variant = "dropdown",
}: {
  variant?: "dropdown" | "sidebar" | "bar";
}) {
  const { user, switchRole } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();

  if (!user || user.isAdmin) return null;

  const role = effectiveRole(user);
  const activeMeta = ROLE_META[role];

  // ---- Dropdown variant (desktop header) ----
  if (variant === "dropdown") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <activeMeta.icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">
              {t(activeMeta.labelKey) !== activeMeta.labelKey ? t(activeMeta.labelKey) : activeMeta.label}
            </span>
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {t("nav.switchRole")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {SWITCHABLE_ROLES.map(({ role: r, icon: Icon, labelKey, label }) => {
            const isActive = user.activeRole === r;
            const isVerified =
              r === "customer" ? true :
              r === "provider" ? user.providerVerified :
              r === "driver" ? user.courierVerified :
              false;
            return (
              <DropdownMenuItem
                key={r}
                onClick={() => {
                  if (isVerified) {
                    switchRole(r);
                  } else {
                    navigate("app/role-application");
                  }
                }}
                className={isActive ? "bg-primary/10" : ""}
              >
                <Icon className="mr-2 h-4 w-4" />
                <span className="flex-1">{t(labelKey) !== labelKey ? t(labelKey) : label}</span>
                {isActive && <span className="text-xs text-primary">●</span>}
                {!isVerified && (
                  <Badge variant="outline" className="ml-1 text-[9px]">Locked</Badge>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // ---- Sidebar variant (inline in sidebar footer) ----
  if (variant === "sidebar") {
    return (
      <div>
        <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("nav.switchRole")}
        </p>
        <div className="flex items-center rounded-lg bg-muted p-0.5">
          {SWITCHABLE_ROLES.map(({ role: r, icon: Icon, labelKey, label }) => {
            const isActive = user.activeRole === r;
            const isVerified =
              r === "customer" ? true :
              r === "provider" ? user.providerVerified :
              r === "driver" ? user.courierVerified :
              false;
            return (
              <button
                key={r}
                onClick={() => {
                  if (isVerified) switchRole(r);
                  else navigate("app/role-application");
                }}
                className={`flex flex-1 flex-col items-center gap-0.5 rounded-md px-1 py-1.5 text-[10px] font-semibold transition ${
                  isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                title={isVerified ? label : `${label} — verify to unlock`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="leading-none">{t(labelKey) !== labelKey ? t(labelKey) : label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-1 px-1 text-[9px] text-muted-foreground">{t("nav.switchRoleHint")}</p>
      </div>
    );
  }

  // ---- Bar variant (mobile sticky tab bar) ----
  return (
    <div className="flex items-center gap-1 rounded-xl bg-muted/60 p-1">
      <div className="flex flex-1 items-center justify-center gap-1 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <RefreshCw className="h-3 w-3" />
        {t("nav.switchRole")}
      </div>
      {SWITCHABLE_ROLES.map(({ role: r, icon: Icon, labelKey, label }) => {
        const isActive = user.activeRole === r;
        const isVerified =
          r === "customer" ? true :
          r === "provider" ? user.providerVerified :
          r === "driver" ? user.courierVerified :
          false;
        return (
          <button
            key={r}
            onClick={() => {
              if (isVerified) switchRole(r);
              else navigate("app/role-application");
            }}
            className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] font-semibold transition ${
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"
            }`}
            aria-label={label}
          >
            <Icon className="h-4 w-4" />
            <span className="leading-none">{t(labelKey) !== labelKey ? t(labelKey) : label}</span>
          </button>
        );
      })}
    </div>
  );
}
